/* eslint-disable no-console */
import _merge from 'lodash/merge';
import { Lightning } from '@lightningjs/sdk';
import Store from 'lightningjs-redux';
import { VIDEO_EVENTS } from '@bedrock/media-engine';
import { Ads } from './ads/ads.component';
import { AdsAdapter } from './ads/ads.adapter';
import { JingleImages } from './jingleImages/jingleImages.component';
import { PAUSE_SIGNAL, PLAY_SIGNAL, SKIP_SIGNAL } from './ads/ads.constants';
import connect from './player.connector';
import { playerDispatcher } from './player.dispatcher';
import { createContentPlayback } from './player.adapter';
import { getInitialStyle } from './player.style';
import { getOriginalUrlFromReference, switchLBannerState } from './player.utils';
import { lifecycleHandler } from './platform/default/lifecycle.handler';
import {
  loadStart,
  paused,
  reset,
  showProgramJingle,
  PLAYER_MODE,
  PROGRAM_JINGLE_DELAY,
} from '../../logic/player/player.constants';
import { isAdPlayerMode } from '../../logic/player/player.selectors';
import { sendEvent } from '../../logic/analytics/analytics.utils';

/**
 * Plays the content with its ads:
 *
 *   source set           → the ads are requested if any, then the content plays once no ad break is on screen
 *   ad break started     → the content engine is released (most of the devices can't decode two videos)
 *   ad break ended       → the content resumes from its last position
 *   source switched      → the content reloads (i.e. the source fallback)
 *   source removed, left → everything is released
 *
 * The content engine is owned by a playback controller, the ad pipeline by the ads adapter.
 * The surfaces follow the player mode: the jingle images cover everything, the ads overlay is shown for the ad modes.
 */
export class Player extends Lightning.Component {
  static _template() {
    return _merge(
      {
        Ads: {
          type: Ads,
          signals: {
            [PAUSE_SIGNAL]: 'pause',
            [PLAY_SIGNAL]: 'play',
            [SKIP_SIGNAL]: 'skipAd',
          },
        },
        // Topmost surface: covers content/ads while a jingle is on screen.
        JingleImages: {
          type: JingleImages,
        },
      },
      getInitialStyle(),
    );
  }

  _construct() {
    this._content = createContentPlayback();
    // NOTE: bumped on every deactivation, so an ad pipeline initialized for a gone playback is dropped
    this._activation = 0;
  }

  _attach() {
    this.stage.gc();
  }

  _active() {
    this._source = null;
    // Last known content position, the content resumes from it
    this._position = undefined;
    // Whether the content was started for the current source, so the next start is a resume
    this._isContentStarted = false;
    this._isPlayingAds = false;
    // Whether the playback is meant to play: set by the play/pause commands, read by the lifecycle handlers
    this._isPlaying = false;
    this.tag('Ads').visible = false;
    this._ads = null;
    this._isActive = true;
    this._unsubscribe = connect(this);
    this.stage.gc();
  }

  _inactive() {
    clearTimeout(this._programJingleTimeout);
    this._isActive = false;
    this._activation += 1;

    if (this._unsubscribe) {
      this._unsubscribe();
      this._unsubscribe = null;
    }

    // NOTE: the store could be cleaned up already (required to avoid so-called "memory leaks" in Metrological envs)
    if (Store.dispatch) {
      Store.dispatch(reset());
    }

    this._stop();
  }

  set source(value) {
    // NOTE: accounting for some Redux/connector stupidity
    if (this._source === value) {
      return;
    }

    const previous = this._source;
    this._source = value;

    if (!value) {
      console.log('[player] Source removed, stopping');
      // NOTE: should be kept to handle player lock during playback (in case of switching of live programs)
      this._stop();
    } else if (previous) {
      console.log('[player] Source switched, reloading the content');
      this._content.release();
      this._isContentStarted = false;
      this._playContent();
    } else {
      console.log('[player] Source set, starting');
      this._start();
    }
  }

  /**
   * The player mode drives the surfaces: an ad mode releases the content and shows the ads overlay,
   * leaving the ad modes resumes the content
   */
  set playerMode(value) {
    clearTimeout(this._programJingleTimeout);

    if (value === PLAYER_MODE.VIDEO_JINGLE) {
      this._programJingleTimeout = setTimeout(() => Store.dispatch(showProgramJingle()), PROGRAM_JINGLE_DELAY);
    }

    // NOTE: the mode changes within a break too (jingle, ads, jingle), only entering or leaving one matters
    const isPlayingAds = isAdPlayerMode(value);
    if (isPlayingAds === this._isPlayingAds) {
      return;
    }

    this._isPlayingAds = isPlayingAds;

    if (isPlayingAds) {
      if (this._content.engine && this._isPlaying) {
        Store.dispatch(paused());
      }

      this._content.release();
    } else {
      this._playContent();
    }

    this.tag('Ads').visible = isPlayingAds;
    this._refocus();
  }

  get isPlayingAds() {
    return this._isPlayingAds;
  }

  set elapsedTimeInSeconds(value) {
    if (this._content.engine) {
      this._position = value;
    }

    if (this._getAdBridge()) {
      this._getAdBridge().contentHasProgressed(value);
    }
  }

  set jingleSurfaceImageId(value) {
    if (value) {
      this.tag('JingleImages').show(value);
    } else {
      this.tag('JingleImages').hide();
    }
  }

  set lifecycleState(value) {
    lifecycleHandler(value, this);
  }

  get hasSource() {
    return Boolean(this._source);
  }

  get isPlaying() {
    return this._isPlaying;
  }

  /** The underlying ad video element while an ad break is on screen (null otherwise). */
  get adVideoElement() {
    return this._getAdBridge() ? this._getAdBridge().videoEl : null;
  }

  play() {
    this.stage.gc();
    this._isPlaying = true;

    if (!(this._isPlayingAds && this._ads && this._ads.play()) && this._content.engine) {
      this._content.engine.play();
    }
  }

  pause() {
    this._isPlaying = false;

    if (!(this._isPlayingAds && this._ads && this._ads.pause()) && this._content.engine) {
      this._content.engine.pause();
    }
  }

  skipAd(skipEntireAdBreak) {
    const adBridge = this._getAdBridge();

    if (adBridge && skipEntireAdBreak) {
      adBridge.skipBreak();
    } else if (adBridge) {
      adBridge.skipAd();
    }
  }

  seek(position) {
    if (!this._content.engine) {
      return;
    }

    this._content.engine.seek(position);
    sendEvent({ key: this._position - position < 0 ? 'forward' : 'backward' });
    this._position = position;

    // NOTE: There are no mid-rolls on Live
    if (!this._source.isLive && this._getAdBridge()) {
      this._getAdBridge().triggerSlotOnSeek(position);
    }
  }

  /**
   * Starts the content again from its beginning on a fresh engine, an ended stream doesn't reliably seek back on some
   * devices. The ad pipeline is left as is, so the ad-engine keeps track of the breaks already watched.
   */
  replay() {
    this._position = 0;
    this._isContentStarted = false;
    this._content.release();
    this._playContent();
  }

  backToLive() {
    if (this._content.engine) {
      this._content.engine.backToLive();
    }
  }

  setAudioTrackById(id) {
    if (this._content.engine) {
      this._content.engine.setAudioTrackById(id);
    }
  }

  setSubtitlesTrackById(id) {
    if (this._content.engine) {
      this._content.engine.setSubtitlesTrackById(id);
    }
  }

  setLBannerState(enable) {
    switchLBannerState(this._content.element, enable);
  }

  /**
   * Restarts the playback once the app is back to the foreground
   */
  resumeContentPlayback() {
    if (this._content.engine) {
      this._startContent(this._content.engine);
    } else if (!this._content.isLoading) {
      this._start();
    }
  }

  _getFocused() {
    return this.tag('Ads').visible ? this.tag('Ads') : undefined;
  }

  _getAdBridge() {
    return this._ads ? this._ads.bridge : null;
  }

  /**
   * Requests the ads of the source if any, otherwise plays the content right away
   */
  async _start() {
    if (this._ads) {
      this._ads.reset();
    }

    const activation = this._activation;
    // NOTE: registered right away, so leaving the player releases the ads even while they are initializing
    const ads = new AdsAdapter();
    this._ads = ads;
    this._content.preload();

    const hasAds = await ads.start(() => {
      if (!this._isContentStarted) {
        this._playContent();
      }
    });

    // NOTE: the player could have been deactivated (and the ads released) while the ads were initializing
    if (activation !== this._activation || ads !== this._ads) {
      ads.reset();
    } else if (hasAds) {
      console.log('[player] Requesting the ads');
      ads.requestAds();
    } else {
      console.log('[player] No ads, playing the content');
      ads.reset();
      this._ads = null;
      this._playContent();
    }
  }

  /**
   * Plays the content unless it's already there, or there is nothing to play
   */
  async _playContent() {
    if (!this._isActive || !this._source || this._isPlayingAds || this._content.engine || this._content.isLoading) {
      return;
    }

    const player = await this._content.load();

    if (player) {
      this._startContent(player);
    }
  }

  /**
   * Starts the source on the given engine: the first start follows the source settings, a resume continues the playback
   */
  async _startContent(player) {
    const source = this._source;
    const isResume = this._isContentStarted;
    this._isPlaying = true;
    this._isContentStarted = true;

    if (!isResume) {
      Store.dispatch(loadStart());
    }

    let reference;
    try {
      reference = await getOriginalUrlFromReference(source.reference);
    } catch (error) {
      console.error('[player] Failed to fetch the manifest redirect', error);

      // NOTE: reported as a fatal playback error, so the next source is tried or the error is shown
      if (player === this._content.engine) {
        playerDispatcher(VIDEO_EVENTS.ERROR, {
          error: { message: 'Failed to fetch the manifest redirect', fatal: true },
        });
      }

      return;
    }

    // NOTE: the content could have been released or replaced while the original URL was requested
    if (player !== this._content.engine || source !== this._source) {
      return;
    }

    // NOTE: the engine fires CAN_PLAY once there is something to play, so the app knows it's OK to start the playback
    player.start({
      ...source,
      reference,
      startTime: Number.isFinite(this._position) ? this._position : source.startTime,
      shouldAutoPlay: isResume || source.shouldAutoPlay,
    });
  }

  _stop() {
    this._content.release();

    if (this._ads) {
      this._ads.reset();
      this._ads = null;
    }

    this._isContentStarted = false;
    this._isPlaying = false;

    sendEvent({ key: 'playbackStopped' });
  }
}
